import React, { useState } from 'react';
import {
    Box,
    Checkbox,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Close as CloseIcon,
    ExpandLess,
    ExpandMore,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import DebouncedLoader from '../common/DebouncedLoader';
import { useBrands } from '../../graphql/pimcore/modules/resourceLibrary/contexts/BrandsProvider';
import { useProductGroups } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { useProductApplications } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductApplicationsProvider';
import { useResourceTypes } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import {
    filterDescriptors,
    FilterName,
    Filters,
} from '../../graphql/pimcore/modules/resourceLibrary/types/filters';
import { usePrefilteredResourceLibraryItems } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';

type MenuItemData = {
    id: string;
    name: string;
    count?: number;
    children?: MenuItemData[];
};

type Category = {
    key: string;
    name: string;
    filterName: FilterName;
    items: MenuItemData[];
};

interface PreFilteredLibraryDrawerProps {
    openDrawer: boolean;
    setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    /** Show counts next to category names */
    showCounts?: boolean;
}

const PreFilteredLibraryDrawer: React.FC<PreFilteredLibraryDrawerProps> = ({
    openDrawer,
    setOpenDrawer,
    showCounts = true,
}) => {
    const { brands, loading: brandsLoading } = useBrands();
    const { productGroups, loading: productGroupsLoading } = useProductGroups();
    const { productApplications, loading: productApplicationsLoading } = useProductApplications();
    const { resourceTypes, loading: resourceTypesLoading } = useResourceTypes();
    const { contentTree: cmsContent } = useCommonContent('ResourceLibrary');
    const { filters, toggleFilter, resetFilters, counts } = usePrefilteredResourceLibraryItems();
    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;

    // Map items to menu structure, filtering out those with no matching resources
    const mapToMenuItems = <T extends { id: string; name: string; children?: T[] }>(
        items: T[],
        countsMap: Map<string, number>
    ): MenuItemData[] =>
        items
            .filter((item) => (countsMap.get(item.id) || 0) > 0)
            .map((item) => ({
                id: item.id,
                name: item.name,
                count: countsMap.get(item.id) || 0,
                children: item.children ? mapToMenuItems(item.children, countsMap) : undefined,
            }));

    const brandsMenuItems = brands ? mapToMenuItems(brands, counts.brands) : [];
    const productGroupsMenuItems = productGroups
        ? mapToMenuItems(productGroups, counts.productGroups)
        : [];
    const productApplicationsMenuItems = productApplications
        ? mapToMenuItems(productApplications, counts.productApplications)
        : [];
    const resourceTypesMenuItems = resourceTypes
        ? mapToMenuItems(resourceTypes, counts.resourceTypes)
        : [];

    const categories: Category[] = [
        {
            key: 'products',
            name: cmsContent.categories.products.content,
            filterName: 'productGroupIds' as FilterName,
            items: productGroupsMenuItems,
        },
        {
            key: 'applications',
            name: cmsContent.categories.applications.content,
            filterName: 'productApplicationIds' as FilterName,
            items: productApplicationsMenuItems,
        },
        {
            key: 'resourceTypes',
            name: cmsContent.categories.resourceType.content,
            filterName: 'resourceTypeIds' as FilterName,
            items: resourceTypesMenuItems,
        },
        {
            key: 'brands',
            name: cmsContent.categories.brands.content,
            filterName: 'brandId' as FilterName,
            items: brandsMenuItems,
        },
    ];

    const [openStates, setOpenStates] = useState<{ [key: string]: boolean }>({});

    const handleClick = (id: string) => {
        setOpenStates((prevStates) => ({
            ...prevStates,
            [id]: !prevStates[id],
        }));
    };

    const isChecked = (
        filterName: keyof typeof filterDescriptors,
        filters: Filters,
        item: MenuItemData
    ) => {
        let checked = false;
        if (filterDescriptors[filterName].isArray) {
            const filterValues = filters[filterName] as string[];
            checked = filterValues.includes(item.id);
        } else {
            const filterValue = filters[filterName] as string | null;
            checked = filterValue === item.id;
        }
        return checked;
    };

    const toggleParentAndChildrenFilters = (filterName: FilterName, item: MenuItemData) => {
        const isParentChecked = isChecked(filterName, filters, item);
        toggleFilter(filterName, item.id);
        item.children
            ?.filter((c) => isParentChecked === isChecked(filterName, filters, c))
            .forEach((c) => toggleFilter(filterName, c.id));

        setOpenStates((prevStates) => {
            const wasOpen = prevStates[item.id] ?? false;
            if (wasOpen === isParentChecked) {
                return {
                    ...prevStates,
                    [item.id]: !prevStates[item.id],
                };
            }
            return prevStates;
        });
    };

    const renderMenuItems = (items: MenuItemData[], filterName: FilterName, level: number = 0) => {
        return items.map((item) => {
            const checked = isChecked(filterName, filters, item);
            return (
                <React.Fragment key={item.id}>
                    <ListItemButton sx={{ pl: 4 * (level + 1) }}>
                        <ListItemIcon sx={{ minWidth: '30px' }}>
                            <Checkbox
                                checked={checked}
                                sx={{
                                    color: level === 0 ? '#E5673E' : 'black',
                                    '&.Mui-checked': { color: level === 0 ? '#B75332' : 'black' },
                                    '&:hover': {
                                        bgcolor: 'rgba(229, 103, 62, 0.08)',
                                        '.MuiSvgIcon-root': {
                                            color: level === 0 ? '#B75332' : 'black',
                                        },
                                    },
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleParentAndChildrenFilters(filterName, item);
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'default',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            toggleParentAndChildrenFilters(filterName, item)
                                        }
                                    >
                                        {showCounts && item.count != null
                                            ? `${item.name} (${item.count})`
                                            : item.name}
                                    </Box>
                                    <Box
                                        component="span"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleClick(item.id)}
                                    >
                                        {item.children?.length ? (
                                            openStates[item.id] ? (
                                                <ExpandLess />
                                            ) : (
                                                <ExpandMore />
                                            )
                                        ) : (
                                            <Box />
                                        )}
                                    </Box>
                                </Box>
                            }
                            sx={{ color: level === 0 ? '#B75332' : 'black' }}
                        />
                    </ListItemButton>
                    {item.children && (
                        <Collapse in={openStates[item.id]} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {renderMenuItems(item.children, filterName, level + 1)}
                            </List>
                        </Collapse>
                    )}
                </React.Fragment>
            );
        });
    };

    const isLoading =
        brandsLoading || productGroupsLoading || productApplicationsLoading || resourceTypesLoading;

    if (isSmallScreen) {
        return (
            <DebouncedLoader loading={isLoading}>
                <Drawer
                    anchor="left"
                    variant="temporary"
                    open={openDrawer}
                    onClose={() => setOpenDrawer(false)}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: '90vw',
                            maxWidth: '400px',
                            position: 'relative',
                            overflowY: 'auto',
                            zIndex: 0,
                        },
                    }}
                >
                    <List sx={{ width: '100%' }}>
                        <ListItem disablePadding sx={{ p: 1, borderBottom: '1px solid #E5673E' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <ListItemText primary={cmsContent.filtersLabel.content} />
                                <Box>
                                    <IconButton onClick={() => resetFilters()}>
                                        <RefreshIcon />
                                        <ListItemText primary={cmsContent.reset.content} />
                                    </IconButton>
                                    <IconButton onClick={() => setOpenDrawer(false)}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </ListItem>
                        {categories
                            .filter((category) => category.items.length > 0)
                            .map((category) => (
                                <Box key={category.key}>
                                    <ListItem disablePadding sx={{ pl: 0 }}>
                                        <ListItemButton
                                            onClick={() => handleClick(category.key)}
                                            sx={{ pl: 0 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: '30px' }} />
                                            <ListItemText primary={category.name} />
                                            {openStates[category.key] ? (
                                                <ExpandLess />
                                            ) : (
                                                <ExpandMore />
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                    <Collapse
                                        in={openStates[category.key]}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        <List component="div" disablePadding>
                                            {renderMenuItems(category.items, category.filterName)}
                                        </List>
                                    </Collapse>
                                    <Divider />
                                </Box>
                            ))}
                    </List>
                </Drawer>
            </DebouncedLoader>
        );
    }

    // 大螢幕使用 permanent Drawer
    return (
        <DebouncedLoader loading={isLoading}>
            <Drawer
                variant="permanent"
                sx={{
                    borderRight: '0.1px solid #E5673E',
                    display: { xs: 'block', sm: 'block' },
                    height: '100%',
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: '100%',
                        position: 'relative',
                        overflowY: 'auto',
                        zIndex: 0,
                    },
                    '::-webkit-scrollbar-track': {
                        background: 'white',
                    },
                    '::-webkit-scrollbar-thumb': {
                        background: '#E5673E',
                        borderRadius: '5px',
                    },
                }}
                open
            >
                <List sx={{ width: '100%' }}>
                    {categories
                        .filter((category) => category.items.length > 0)
                        .map((category) => (
                            <Box key={category.key}>
                                <ListItem disablePadding sx={{ pl: 0 }}>
                                    <ListItemButton
                                        onClick={() => handleClick(category.key)}
                                        sx={{ pl: 0 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: '30px' }} />
                                        <ListItemText primary={category.name} />
                                        {openStates[category.key] ? <ExpandLess /> : <ExpandMore />}
                                    </ListItemButton>
                                </ListItem>
                                <Collapse
                                    in={openStates[category.key]}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <List component="div" disablePadding>
                                        {renderMenuItems(category.items, category.filterName)}
                                    </List>
                                </Collapse>
                            </Box>
                        ))}
                </List>
            </Drawer>
        </DebouncedLoader>
    );
};

export default PreFilteredLibraryDrawer;
